import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, ChevronDown } from 'lucide-react';

const PETIT_PAGE_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='48' fill='%231a365d' stroke='%23d4af37' stroke-width='3'/%3E%3Ctext x='50' y='60' text-anchor='middle' fill='%23d4af37' font-size='40'%3E👤%3C/text%3E%3C/svg%3E";

// Detect language from browser
const detectLanguage = () => {
  const browserLang = navigator.language?.split('-')[0] || 'fr';
  const supported = ['fr', 'en', 'es', 'de', 'it', 'ja'];
  return supported.includes(browserLang) ? browserLang : 'fr';
};

// Translations
const translations = {
  fr: {
    title: "Le Petit Page",
    subtitle: "Assistant de Versailles",
    placeholder: "Posez votre question...",
    welcome: "Bonjour ! Je suis le Petit Page de Versailles. Comment puis-je vous aider aujourd'hui ?",
    examples: [
      "Comment visiter le Château ?",
      "Les épiceries ferment à quelle heure ?",
      "Où manger près du Château ?"
    ]
  },
  en: {
    title: "The Little Page",
    subtitle: "Versailles Assistant",
    placeholder: "Ask your question...",
    welcome: "Hello! I'm the Little Page of Versailles. How can I help you today?",
    examples: [
      "How to visit the Palace?",
      "What are the opening hours?",
      "Where to eat near the Palace?"
    ]
  },
  es: {
    title: "El Pequeño Paje",
    subtitle: "Asistente de Versalles",
    placeholder: "Haga su pregunta...",
    welcome: "¡Hola! Soy el Pequeño Paje de Versalles. ¿Cómo puedo ayudarle hoy?",
    examples: [
      "¿Cómo visitar el Palacio?",
      "¿Cuáles son los horarios?",
      "¿Dónde comer cerca del Palacio?"
    ]
  },
  de: {
    title: "Der Kleine Page",
    subtitle: "Versailles Assistent",
    placeholder: "Stellen Sie Ihre Frage...",
    welcome: "Hallo! Ich bin der Kleine Page von Versailles. Wie kann ich Ihnen heute helfen?",
    examples: [
      "Wie besuche ich das Schloss?",
      "Was sind die Öffnungszeiten?",
      "Wo kann man in der Nähe essen?"
    ]
  },
  it: {
    title: "Il Piccolo Paggio",
    subtitle: "Assistente di Versailles",
    placeholder: "Fai la tua domanda...",
    welcome: "Ciao! Sono il Piccolo Paggio di Versailles. Come posso aiutarti oggi?",
    examples: [
      "Come visitare il Palazzo?",
      "Quali sono gli orari?",
      "Dove mangiare vicino al Palazzo?"
    ]
  },
  ja: {
    title: "小さな従者",
    subtitle: "ヴェルサイユアシスタント",
    placeholder: "質問をどうぞ...",
    welcome: "こんにちは！私はヴェルサイユの小さな従者です。今日は何をお手伝いしましょうか？",
    examples: [
      "宮殿の訪問方法は？",
      "営業時間は？",
      "宮殿の近くで食事は？"
    ]
  }
};

const PetitPageBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [language, setLanguage] = useState(detectLanguage());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const t = translations[language] || translations.fr;

  // Initialize session
  useEffect(() => {
    const storedSession = localStorage.getItem('petitPageSession');
    if (storedSession) {
      setSessionId(storedSession);
    } else {
      const newSession = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('petitPageSession', newSession);
      setSessionId(newSession);
    }
  }, []);

  // Add welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: t.welcome,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, t.welcome, messages.length]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/bot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText.trim(),
          session_id: sessionId,
          language: language
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        sources: data.sources,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'fr' 
          ? "Désolé, une erreur s'est produite. Veuillez réessayer."
          : "Sorry, an error occurred. Please try again.",
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleExampleClick = (example) => {
    sendMessage(example);
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: t.welcome,
      timestamp: new Date()
    }]);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-110 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%)',
          border: '3px solid #d4af37'
        }}
        aria-label="Ouvrir l'assistant"
        data-testid="petit-page-open-btn"
      >
        <img
          src="/images/petit-page-bot.png"
          alt="Petit Page"
          className="w-full h-full rounded-full object-cover"
          onError={(e) => { e.target.src = PETIT_PAGE_IMAGE; }}
        />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 transform ${
          isOpen 
            ? 'scale-100 opacity-100 pointer-events-auto' 
            : 'scale-95 opacity-0 pointer-events-none'
        }`}
        style={{ width: '380px', maxWidth: 'calc(100vw - 48px)' }}
      >
        <div 
          className="rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ 
            height: '600px', 
            maxHeight: 'calc(100vh - 100px)',
            background: '#fff'
          }}
        >
          {/* Header */}
          <div 
            className="p-4 flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%)',
              borderBottom: '3px solid #d4af37'
            }}
          >
            <div className="relative">
              <img
                src="/images/petit-page-bot.png"
                alt="Petit Page"
                className="w-12 h-12 rounded-full border-2 border-amber-400"
                onError={(e) => { e.target.src = PETIT_PAGE_IMAGE; }}
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">{t.title}</h3>
              <p className="text-amber-200 text-sm">{t.subtitle}</p>
            </div>
            
            {/* Language Selector */}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-white/20 text-white text-sm rounded px-2 py-1 border border-white/30"
              data-testid="language-selector"
            >
              <option value="fr">🇫🇷 FR</option>
              <option value="en">🇬🇧 EN</option>
              <option value="es">🇪🇸 ES</option>
              <option value="de">🇩🇪 DE</option>
              <option value="it">🇮🇹 IT</option>
              <option value="ja">🇯🇵 JA</option>
            </select>
            
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1 transition-colors"
              aria-label="Fermer"
              data-testid="petit-page-close-btn"
            >
              <X size={24} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-blue-900 text-white rounded-br-md'
                      : msg.isError
                        ? 'bg-red-100 text-red-800 rounded-bl-md'
                        : 'bg-white text-gray-800 shadow-md rounded-bl-md border border-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-medium">Sources:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {msg.sources.slice(0, 3).map((src, i) => (
                          <span 
                            key={i} 
                            className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"
                          >
                            {src.type === 'arrete' ? '📜' : src.type === 'guide' ? '📖' : src.type === 'faq' ? '❓' : '📰'} {src.title?.slice(0, 30)}...
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-md border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Examples (show only when few messages) */}
          {messages.length <= 2 && !isLoading && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">💡 Suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {t.examples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example)}
                    className="text-xs bg-white border border-amber-300 text-amber-800 px-3 py-1.5 rounded-full hover:bg-amber-50 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form 
            onSubmit={handleSubmit}
            className="p-4 border-t border-gray-200 bg-white"
          >
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.placeholder}
                disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 text-sm"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: input.trim() ? 'linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%)' : '#e5e7eb'
                }}
                data-testid="chat-submit"
              >
                <Send size={20} className={input.trim() ? 'text-white' : 'text-gray-400'} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PetitPageBot;
